import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { toast } from 'react-toastify';
import {
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  SectionHeader,
} from '../../components/ui';
import {
  changedServiceRules,
  fromServiceForm,
  serviceCancellationExample,
  serviceRulesOf,
  toServiceForm,
  type ServiceRulesForm,
} from './serviceRulesAdmin';

const Example = styled.ul`
  ${tw`text-sm mt-4 pl-4 list-disc`}
  color: var(--color-text-secondary);
`;

function isStale(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'BRAND_RULES_STALE'));
}

/** Server field errors (`details.errors[].path`, e.g. `serviceCustomerCancellation.lateFeePercent`). */
const PATH_TO_FIELD: Record<string, keyof ServiceRulesForm> = {
  'serviceCustomerCancellation.freeBeforeHours': 'freeBeforeHours',
  'serviceCustomerCancellation.lateFeePercent': 'lateFeePercent',
  'serviceCustomerCancellation.startedFeePercent': 'startedFeePercent',
  serviceAutoConfirmHours: 'autoConfirmHours',
  serviceRefundWindowHours: 'refundWindowHours',
  serviceLateCancelLimit: 'lateCancelLimit',
  serviceNoShowLimit: 'noShowLimit',
};

function serverFieldErrors(error: unknown): Partial<Record<keyof ServiceRulesForm, string>> {
  const out: Partial<Record<keyof ServiceRulesForm, string>> = {};
  const list = (error as any)?.graphQLErrors?.[0]?.extensions?.details?.errors;
  if (!Array.isArray(list)) return out;
  for (const item of list) {
    const field = PATH_TO_FIELD[String(item?.path ?? '').replace(/^bookingRules\./, '')];
    if (field) out[field] = String(item?.message ?? 'Not allowed');
  }
  return out;
}

/**
 * Spec 019 W23a (D13, D17, D29, D33): cleaning and handover job rules for a
 * brand that sells them, on `/admin/brand-settings`. Saved with the loaded
 * `version` through `updateBrandRules`; jobs keep the rules they were booked
 * under.
 */
export function ServiceRulesSection({ brandName }: { brandName: string }) {
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation({
    refetchQueries: ['BrandRulesHistory'],
  });
  const loaded = useMemo(() => serviceRulesOf(data?.brandRules?.bookingRules), [data]);
  const [form, setForm] = useState<ServiceRulesForm>(() => toServiceForm(loaded));
  const [serverErrors, setServerErrors] = useState<Partial<Record<keyof ServiceRulesForm, string>>>(
    {},
  );
  useEffect(() => setForm(toServiceForm(loaded)), [loaded]);

  const parsed = fromServiceForm(form);
  const errors = { ...serverErrors, ...(parsed.errors ?? {}) };
  const changes = parsed.rules ? changedServiceRules(loaded, parsed.rules) : {};
  const dirty = Object.keys(changes).length > 0;
  const example = serviceCancellationExample(parsed.rules ?? loaded);

  const set = (key: keyof ServiceRulesForm, value: string) => {
    setServerErrors(current => ({ ...current, [key]: undefined }));
    setForm(current => ({ ...current, [key]: value }));
  };

  const save = async () => {
    if (!data?.brandRules || !dirty || !parsed.rules) return;
    try {
      await updateBrandRules({
        variables: { input: { version: data.brandRules.version, bookingRules: changes } },
      });
      toast.success('Saved. New jobs use these rules within about a minute.');
      await refetch();
    } catch (e) {
      if (isStale(e)) {
        toast.error(
          'Someone else changed these rules. The latest values are loaded; check them and save again.',
        );
        await refetch();
        return;
      }
      setServerErrors(serverFieldErrors(e));
      toast.error((e as Error).message || 'Could not save service rules');
    }
  };

  const numberField = (
    key: keyof ServiceRulesForm,
    label: string,
    hint: string,
    id = `service-${key}`,
  ) => (
    <Field label={label} htmlFor={id} hint={hint} error={errors[key]}>
      <Input
        id={id}
        inputMode="decimal"
        value={form[key]}
        onChange={e => set(key, e.target.value)}
        data-testid={id}
      />
    </Field>
  );

  return (
    <Card data-testid="service-rules">
      <CardHeader
        title="Service rules"
        description={`Cancellation fees, confirmation and refund windows, and worker strikes for cleaning and handover jobs on ${brandName}. Jobs already booked keep the rules they were booked under.`}
      />
      {loading && <LoadingBlock label="Loading service rules" />}
      {error && (
        <CardBody>
          <Alert tone="error" title="Could not load service rules">
            {error.message}
          </Alert>
        </CardBody>
      )}
      {!loading && !error && (
        <CardBody>
          <SectionHeader title="Customer cancels" level={3} />
          <FormGrid $cols={3}>
            {numberField(
              'freeBeforeHours',
              'Free until (hours before the window)',
              '0 to 168. After this a fee applies.',
            )}
            {numberField(
              'lateFeePercent',
              'Late fee (%)',
              'Of the job, after the free time. 0 to 100.',
            )}
            {numberField(
              'startedFeePercent',
              'Fee once the window started (%)',
              'Of the job. 0 to 100.',
            )}
          </FormGrid>
          <Example data-testid="service-cancellation-example">
            <li>{example.free}</li>
            {example.late && <li>{example.late}</li>}
            <li>{example.started}</li>
            <li>
              The service fee and tax are charged in the same share. A handover counts as one hour.
            </li>
          </Example>
          <SectionHeader title="After the job" level={3} />
          <FormGrid $cols={2}>
            {numberField(
              'autoConfirmHours',
              'Auto-confirm after (hours)',
              'If the customer does not confirm or report a problem. 1 to 168.',
            )}
            {numberField(
              'refundWindowHours',
              'Refund requests until (hours after confirming)',
              '0 to 720. 0 turns refund requests off.',
            )}
          </FormGrid>
          <SectionHeader title="Worker strikes (90 days)" level={3} />
          <FormGrid $cols={2}>
            {numberField(
              'lateCancelLimit',
              'Late cancellations before a pause',
              'The worker is paused at this count. 1 to 20.',
            )}
            {numberField(
              'noShowLimit',
              'No-shows before a pause',
              'The worker is paused at this count. 1 to 20.',
            )}
          </FormGrid>
        </CardBody>
      )}
      <CardFooter>
        <Button
          theme="filled"
          text="Save service rules"
          onClick={save}
          isDisabled={!dirty || saving || !parsed.rules}
          isLoading={saving}
          dataTestId="service-rules-save"
        />
      </CardFooter>
    </Card>
  );
}

export default ServiceRulesSection;
